import { c as _c } from "react/compiler-runtime";
import Image from 'next/image';
import Link from 'next/link';
import { useState, useRef } from 'react';
import { useRouter } from 'next/router';
import { Formik } from 'formik';
import * as yup from 'yup';
import { usePostHog, useFeatureFlagEnabled } from 'posthog-js/react';
import { posthogConsent } from '@/lib/trackingConsent';
import logger from '@/utils/logger';
import Field from '../Field.js';
import TurnstileField from '../TurnstileField.tsx';
import { turnstileTokenSchema } from '@/hooks/useTurnstile';
import Button from '@helios/Button';
import Spinner from '@helios/Spinner';
import { usePardot } from '../Pardot.js';
import useCampus from '@/hooks/useCampus.ts';
import { APPLICATION_HERO } from './applicationHero';
import classNames from 'classnames';
import FormActions from '@helios/FormActions';
import PageHero from '@helios/PageHero';
import Tooltip from '@/components/Tooltip.js';
import FieldGroup from '@/components/FieldGroup.js';
import RegularArrowTurnRightIcon from '@/components/icons/regular/arrow-turn-right';
import SolidArrowsRotateIcon from '@/components/icons/solid/arrows-rotate';
import SolidCircleQuestionIcon from '@/components/icons/solid/circle-question';
// Google's "G" from its Sign in with Google branding assets (signin-assets.zip,
// "Android + Web/SVG/Light/Theme=Light, Show text=No, Shape=Square"), cropped
// to the logo and rendered to a transparent 96px PNG. The SVG draws its colour
// sweep with an HTML gradient in a <foreignObject>, which Safari rasterises at
// low resolution in an <img>.
import GoogleLogo from '@/public/images/google-logo.png';
import styles from './ApplicationForm.module.scss';
import { captureAnalyticsEvent, captureLegacyAnalyticsEvent } from '@/lib/analytics';
import { APPLICATION_AUTH_CONTENT } from '@/utils/applicationAuthContent';
import { getApplicationMagicLink } from '@/utils/helpers.js';
const SPARC_PARTNER_ID = '5e8f2b13-be85-41c4-9693-afefbe576103';

// Apple's logo, drawn by its button image service (Sign in with Apple REST
// API, logo only): its black mark on Apple's white, which the dark theme
// inverts to Apple's white on black.
const APPLE_LOGO_SRC = 'https://appleid.cdn-apple.com/appleid/button/logo?size=40&color=white&border=false&scale=3';

// The single sign-on choices above the email, one under the other, then "or
// use your email". The buttons are not wired up yet. Each shows its
// provider's own logo beside our label. Once one signs in, its click starts
// the application under the consent blurb below it, so it must grant the
// application consent as the submit does.
function SsoOptions() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className={styles.sso}><div className={styles.sso__buttons}>{[["Google", GoogleLogo], ["Apple", APPLE_LOGO_SRC]].map(_temp)}</div><p className={styles.sso__divider}>{APPLICATION_AUTH_CONTENT.ssoDivider}</p></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/**
 * @param {object} props
 * @param {object | null} [props.employer] The partner, on a co-brand host.
 * @param {boolean} [props.photoUnderHeader] The campus header's clear bar sits
 *   over the photo's top (the page veils it there).
 */
function _temp(t0) {
  const [provider, logo] = t0;
  return <Button key={provider} type="button" variant="outline" tone="neutral" fullWidth={true} iconLeft={<Image alt="" className={styles[`sso__logo--${provider.toLowerCase()}`]} height={40} referrerPolicy="no-referrer" sizes="40px" src={logo} unoptimized={true} width={40} />}>Continue with {provider}</Button>;
}
function ApplicationFormAuth(t0) {
  const $ = _c(50);
  const {
    employer,
    photoUnderHeader: t1
  } = t0;
  const photoUnderHeader = t1 === undefined ? false : t1;
  const {
    sendMagicLink
  } = useCampus();
  const campaignParams = usePardot();
  const posthog = usePostHog();
  const router = useRouter();
  const queryRef = router.isReady ? Array.isArray(router.query.ref) ? router.query.ref[0] : router.query.ref : undefined;
  const turnstileEnabled = useFeatureFlagEnabled("turnstile-verification");
  const ssoEnabled = useFeatureFlagEnabled("sso-login-buttons") === true;
  const turnstileRef = useRef(null);
  let t2;
  if ($[0] !== turnstileEnabled) {
    t2 = t3 => {
      const {
        setFieldValue,
        setFieldTouched
      } = t3;
      if (!turnstileEnabled) {
        return;
      }
      setFieldValue("turnstileToken", "", false);
      setFieldTouched("turnstileToken", false, false);
      turnstileRef.current?.reset();
    };
    $[0] = turnstileEnabled;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const resetToken = t2;
  const [step, setStep] = useState(1);
  const [resending, setResending] = useState(false);
  const [continueExisting, setContinueExisting] = useState(false);
  const [eligibilityCheckFailed, setEligibilityCheckFailed] = useState(false);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Link href="/terms-of-use">{APPLICATION_AUTH_CONTENT.termsLabel}</Link>;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className={styles.consent}>{APPLICATION_AUTH_CONTENT.consentPrefix}{" "}{t3}{" "}and{" "}<Link href="/privacy-policy">{APPLICATION_AUTH_CONTENT.privacyLabel}</Link>.</div>;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const consent = t4;
  const t5 = employer && styles["photo-split--below-header"];
  const t6 = photoUnderHeader && styles["photo-split--under-header"];
  let t7;
  if ($[4] !== t5 || $[5] !== t6) {
    t7 = classNames(styles["photo-split"], t5, t6);
    $[4] = t5;
    $[5] = t6;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <PageHero.Image src={APPLICATION_HERO.image} alt="Apply today" frame="portrait" />;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <PageHero.Media placement="split">{t8}<PageHero.Caption><p>{APPLICATION_HERO.graduateName}</p><p><small>Campus Graduate</small></p></PageHero.Caption></PageHero.Media>;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] !== employer) {
    t10 = employer ? {
      employeeId: ""
    } : {};
    $[9] = employer;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] !== turnstileEnabled) {
    t11 = turnstileEnabled ? {
      turnstileToken: ""
    } : {};
    $[11] = turnstileEnabled;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== t10 || $[14] !== t11) {
    t12 = {
      email: "",
      ...t10,
      ...t11
    };
    $[13] = t10;
    $[14] = t11;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== continueExisting || $[17] !== employer || $[18] !== turnstileEnabled) {
    t13 = employer && !continueExisting ? yup.object().shape({
      email: yup.string().email().required(),
      employeeId: yup.string().required(),
      ...(turnstileEnabled ? {
        turnstileToken: turnstileTokenSchema
      } : {})
    }) : yup.object().shape({
      email: yup.string().email().required(),
      ...(turnstileEnabled ? {
        turnstileToken: turnstileTokenSchema
      } : {})
    });
    $[16] = continueExisting;
    $[17] = employer;
    $[18] = turnstileEnabled;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== campaignParams || $[21] !== continueExisting || $[22] !== employer || $[23] !== posthog || $[24] !== queryRef || $[25] !== resetToken || $[26] !== sendMagicLink || $[27] !== turnstileEnabled) {
    t14 = async (values, t15) => {
      const {
        setFieldError,
        setFieldTouched: setFieldTouched_0,
        setFieldValue: setFieldValue_0
      } = t15;
      if (posthog) {
        posthogConsent.grantApplication(posthog);
      }
      captureLegacyAnalyticsEvent(posthog, "App Authentication Request Submitted", {
        email: values.email,
        flow: employer ? "employer" : "standard",
        action: continueExisting ? "continue_existing" : "new_application"
      });
      const redirectURI = getApplicationMagicLink(window.location.origin, campaignParams, undefined, queryRef ?? undefined);
      if (employer && !continueExisting) {
        await sendMagicLink({
          email: values.email,
          ...(turnstileEnabled ? {
            turnstileToken: values.turnstileToken
          } : {}),
          employeeId: values.employeeId,
          partnerId: SPARC_PARTNER_ID,
          redirectURI
        }, campaignParams).then(resp => {
          if (resp.status === 200) {
            setStep(_temp2);
          }
        }).catch(err => {
          setEligibilityCheckFailed(true);
          resetToken({
            setFieldValue: setFieldValue_0,
            setFieldTouched: setFieldTouched_0
          });
          captureAnalyticsEvent(posthog, "application_flow_failed", {
            failure_type: "eligibility",
            stage: "authentication"
          });
          logger.error(err, {
            description: "Eligibility check failed",
            source: "ApplicationFormAuth"
          });
        });
      } else {
        await sendMagicLink({
          email: values.email,
          ...(turnstileEnabled ? {
            turnstileToken: values.turnstileToken
          } : {}),
          redirectURI
        }, campaignParams).then(() => {
          setStep(_temp3);
        }).catch(err_0 => {
          const errorMessage = err_0?.data?.message || "Something went wrong. Please try again.";
          setFieldError("email", errorMessage);
          setFieldTouched_0("email", true, false);
          resetToken({
            setFieldValue: setFieldValue_0,
            setFieldTouched: setFieldTouched_0
          });
          captureAnalyticsEvent(posthog, "application_flow_failed", {
            failure_type: "magic_link",
            stage: "authentication"
          });
          logger.error(err_0, {
            description: "Failed to send magic link",
            source: "ApplicationFormAuth"
          });
        });
      }
    };
    $[20] = campaignParams;
    $[21] = continueExisting;
    $[22] = employer;
    $[23] = posthog;
    $[24] = queryRef;
    $[25] = resetToken;
    $[26] = sendMagicLink;
    $[27] = turnstileEnabled;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== campaignParams || $[30] !== continueExisting || $[31] !== eligibilityCheckFailed || $[32] !== employer || $[33] !== posthog || $[34] !== queryRef || $[35] !== resending || $[36] !== sendMagicLink || $[37] !== ssoEnabled || $[38] !== step || $[39] !== turnstileEnabled) {
    t15 = formik => {
      const renderEmailStep = submitLabel => <>{ssoEnabled && <SsoOptions />}<FieldGroup label={ssoEnabled ? APPLICATION_AUTH_CONTENT.emailLabel : APPLICATION_AUTH_CONTENT.applicationPageEmailLabel}><Field type="email" name="email" placeholder={ssoEnabled ? APPLICATION_AUTH_CONTENT.emailPlaceholder : undefined} autoFocus={true} /></FieldGroup>{consent}{turnstileEnabled && <TurnstileField formik={formik} ref={turnstileRef} />}<FormActions stretch={false} className={styles.auth__actions}><Button type="submit" disabled={formik.isSubmitting} arrow={true}>{submitLabel}</Button></FormActions></>;
      return <form onSubmit={formik.handleSubmit}>{step === 1 && continueExisting && <div className={classNames(styles.auth, styles["auth--continue"])}><h1 className={styles["screen-title"]}>Continue your application.</h1><p className={classNames(styles.lead, styles["lead--large"])}>{ssoEnabled ? APPLICATION_AUTH_CONTENT.continueSsoDescription : APPLICATION_AUTH_CONTENT.continueDescription}</p>{renderEmailStep("Continue")}<Button type="button" variant="text" tone="neutral" onClick={() => setContinueExisting(false)} iconRight={<RegularArrowTurnRightIcon />}>Start a new application instead</Button></div>}{step === 1 && !continueExisting && !employer && <div className={styles.auth}><h1 className={styles["screen-title"]}>{APPLICATION_AUTH_CONTENT.initialTitle}</h1><p className={styles.lead}>{APPLICATION_AUTH_CONTENT.initialDescription}</p>{renderEmailStep("Get started")}<Button type="button" variant="text" tone="neutral" onClick={() => setContinueExisting(true)} arrow={true}>Continue an existing application</Button></div>}{step === 1 && !continueExisting && !!employer && <div className={styles.auth}><h1 className={styles["screen-title"]}>Apply today.</h1><p className={classNames(styles.lead, styles["lead--large"])}>We want to get to know you. This should take about 15 minutes.</p><FieldGroup label={<>Employee ID<Tooltip theme="descriptive" delay={0} placement="top" distance={5} maxWidth="250px" content={<span>Please use your ADP or Dayforce ID # provided by your manager.</span>}><span className={styles["tooltip-trigger"]}><SolidCircleQuestionIcon /></span></Tooltip></>} error={eligibilityCheckFailed ? <>We couldn't verify your employment status. Please try again, or email us at{" "}<a href="mailto:forever21@campus.edu">forever21@campus.edu</a>{" "}if you think we've made a mistake.</> : undefined}><Field type="string" name="employeeId" autoFocus={true} /></FieldGroup><FieldGroup label={APPLICATION_AUTH_CONTENT.applicationPageEmailLabel}><Field type="email" name="email" /></FieldGroup>{consent}{turnstileEnabled && <TurnstileField formik={formik} ref={turnstileRef} />}<FormActions stretch={false} className={styles.auth__actions}><Button type="submit" disabled={formik.isSubmitting} arrow={true}>Get started</Button></FormActions><Button type="button" variant="text" tone="neutral" onClick={() => setContinueExisting(true)} arrow={true}>Continue an existing application</Button></div>}{step === 2 && <div className={classNames(styles.auth, styles["auth--sent"])}><div className={styles.sent}>{!resending && <><h1 className={styles["screen-title"]}>Check your email.</h1><p className={classNames(styles.lead, styles["lead--large"], styles["lead--narrow"])}>Click on the link in the email we just sent to{" "}<strong>{formik.values.email}</strong> to continue your application.</p><div className={styles.sent__help}><Button type="button" variant="text" tone="neutral">Didn't receive the email?</Button></div></>}{resending && <div className={styles.sent__spinner}><Spinner /></div>}</div><div className={styles.sent__actions}><Button type="button" disabled={resending} iconRight={<SolidArrowsRotateIcon />} onClick={async () => {
              const redirectURI_0 = getApplicationMagicLink(window.location.origin, campaignParams, undefined, queryRef ?? undefined);
              setResending(true);
              const resendBody = employer ? {
                email: formik.values.email,
                redirectURI: redirectURI_0,
                employeeId: formik.values.employeeId,
                partnerId: SPARC_PARTNER_ID
              } : {
                email: formik.values.email,
                redirectURI: redirectURI_0
              };
              await sendMagicLink(resendBody, campaignParams).catch(err_1 => {
                formik.setFieldError("email", "Failed to resend. Please try again.");
                formik.setFieldTouched("email", true, false);
                captureAnalyticsEvent(posthog, "application_flow_failed", {
                  failure_type: "magic_link",
                  stage: "authentication"
                });
                logger.error(err_1, {
                  description: "Failed to resend magic link",
                  source: "ApplicationFormAuth"
                });
              });
              setTimeout(() => setResending(false), 1000);
            }}>Resend the email</Button><span className={styles.sent__or}>Or</span><Button type="button" variant="outline" tone="neutral" onClick={() => setStep(1)}>Change email address</Button></div></div>}</form>;
    };
    $[29] = campaignParams;
    $[30] = continueExisting;
    $[31] = eligibilityCheckFailed;
    $[32] = employer;
    $[33] = posthog;
    $[34] = queryRef;
    $[35] = resending;
    $[36] = sendMagicLink;
    $[37] = ssoEnabled;
    $[38] = step;
    $[39] = turnstileEnabled;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== continueExisting || $[42] !== t12 || $[43] !== t13 || $[44] !== t14 || $[45] !== t15) {
    t16 = <PageHero.Content placement="split"><Formik key={continueExisting} validateOnBlur={false} initialValues={t12} validationSchema={t13} onSubmit={t14}>{t15}</Formik></PageHero.Content>;
    $[41] = continueExisting;
    $[42] = t12;
    $[43] = t13;
    $[44] = t14;
    $[45] = t15;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] !== t16 || $[48] !== t7) {
    t17 = <PageHero><PageHero.Container width="full"><div className={t7}>{t9}{t16}</div></PageHero.Container></PageHero>;
    $[47] = t16;
    $[48] = t7;
    $[49] = t17;
  } else {
    t17 = $[49];
  }
  return t17;
}
function _temp3(s_0) {
  return s_0 + 1;
}
function _temp2(s) {
  return s + 1;
}
export default ApplicationFormAuth;